import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import type { RootState, AppDispatch } from "../../store";
import { logout } from "../../store/slices/authSlice";
import { fetchColleges } from "../../store/slices/collegeSlice";
import { fetchStudents } from "../../store/slices/studentSlice";
import { fetchFeePayments } from "../../store/slices/feeSlice";
import { fetchUserStats } from "../../store/slices/usersSlice";
import AdminLayout from "../layout/AdminLayout";
import CollegeAdminLayout from "../layout/CollegeAdminLayout";
import StaffLayout from "../layout/StaffLayout";
import AccountantAdminLayout from "../layout/AccountantAdminLayout";
import Analytics from "../admin/Analytics";
import Colleges from "../admin/Colleges";
import Students from "../admin/Students";
import Users from "../admin/Users";
import Payments from "../admin/Payments";
import Reports from "../admin/Reports";
import Settings from "../admin/Settings";
import Notifications from "../admin/Notifications";
import CollegeAnalytics from "../college/CollegeAnalytics";
import CollegeStudents from "../college/CollegeStudents";
import CollegeGroups from "../college/CollegeGroups";
import CollegeUsers from "../college/CollegeUsers";
import CollegePayments from "../college/CollegePayments";
import CollegeReports from "../college/CollegeReports";
import CollegeNotifications from "../college/CollegeNotifications";
import CollegeSettings from "../college/CollegeSettings";
import StaffDashboard from "../staff/StaffDashboard";
import StaffAnalytics from "../staff/StaffAnalytics";
import StaffStudents from "../staff/StaffStudents";
import StaffReports from "../staff/StaffReports";
import StaffSettings from "../staff/StaffSettings";
import AccountantAnalytics from "../accountant/AccountantAnalytics";
import AccountantStudents from "../accountant/AccountantStudents";
import AccountantPayments from "../accountant/AccountantPayments";
import AccountantReports from "../accountant/AccountantReports";
import AccountantNotifications from "../accountant/AccountantNotifications";
import AccountantSettings from "../accountant/AccountantSettings";
import { api } from "@/services/api";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Building,
  DollarSign,
  TrendingUp,
  Users as UsersIcon,
  Settings as SettingsIcon,
  LucideProps,
  HandCoins,
  Shield,
  IndianRupee,
  BookOpen,
  Hourglass,
  Calendar,
} from "lucide-react";
import { formatCurrency } from "@/lib/model";
import { PaymentRecordsTable } from "./PaymentRecordsTable";

interface CollegeDashboardMetrics {
  totalStudents: number;
  activeStudents: number;
  activePrograms: number;
  totalRevenue: number;
  periodRevenue: number;
  previousRevenue: number;
  totalFees: number;
  totalPaid: number;
  pendingAmount: number;
  revenueGrowth: number;
  collectionsToday: number;
  totalPrograms: number;
}

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, isLoading } = useSelector((state: RootState) => state.auth);
  const { pagination: collegePagination } = useSelector(
    (state: RootState) => state.college
  );
  const { pagination: studentPagination } = useSelector(
    (state: RootState) => state.student
  );
  const { payments, pagination: feePagination } = useSelector(
    (state: RootState) => state.fee
  );
  const { stats: userStats } = useSelector((state: RootState) => state.users);
  const [collegeDashboardMetrics, setCollegeDashboardMetrics] =
    useState<CollegeDashboardMetrics | null>(null);

  const [currentSection, setCurrentSection] = useState("dashboard");

  const fetchDashboardMetrics = async () => {
    try {

      const response = (await api.get(`financial/dashboard-metrics?timeFilter=monthly`)).data;
      // const response = await fetch(
      //   `/api/financial/dashboard-metrics?timeFilter=monthly`,
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );


      if (!response.success) {
        throw new Error("Failed to fetch dashboard metrics");
      }

      // const data = await response.json();
      if (response.success) {
        setCollegeDashboardMetrics(response.data);
      } else {
        console.error(response.message || "Failed to load metrics");
      }
    } catch (err) {
      console.error("Error fetching dashboard metrics:", err);
    }
  };

  useEffect(() => {
    async function fetchCollegeMetrics() {
      await fetchDashboardMetrics();
    }

    if (user) {
      // Fetch dashboard data based on user role
      if (user.role === "SYSTEM_ADMIN") {
        dispatch(fetchColleges({ limit: 100 })); // Get all colleges for accurate count
        dispatch(fetchStudents({ page: 1, limit: 1 })); // Just to get count
        dispatch(fetchFeePayments({ page: 1, limit: 1 })); // Just to get stats
        dispatch(fetchUserStats()); // Get user statistics
      } else if (user.role === "COLLEGE_ADMIN") {
        dispatch(fetchUserStats());
        fetchCollegeMetrics();
      } else if (user.role === "STAFF") {
        dispatch(fetchStudents({ page: 1, limit: 1 })); // Just to get count for staff dashboard
      } else if (user.role === "ACCOUNTANT") {
        dispatch(fetchStudents({ page: 1, limit: 1 })); // Just to get count for accountant dashboard
        dispatch(fetchFeePayments({ page: 1, limit: 1 })); // Just to get stats for financial dashboard
      } else if (user.role === "STUDENT") {
        dispatch(fetchFeePayments({ page: 1, limit: 10 })); // Get student's payments
      }
    }
  }, [dispatch, user]);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleSectionChange = (section: string) => {
    setCurrentSection(section);
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalRevenue = payments.reduce(
    (sum, payment) => sum + Number(payment.amountPaid),
    0
  );
  const studentCount = studentPagination.total;
  const totalPayments = feePagination.total;

  // Render different sections based on user role and current section
  const renderContent = () => {
    // For SYSTEM_ADMIN, use the modern AdminLayout with sections
    if (user.role === "SYSTEM_ADMIN") {
      switch (currentSection) {
        case "analytics":
          return <Analytics />;
        case "colleges":
          return <Colleges />;
        case "dashboard":
          return <SystemAdminDashboard />;
        case "students":
          return <Students />;
        case "users":
          return <Users />;
        case "payments":
          return <Payments />;
        case "reports":
          return <Reports />;
        case "notifications":
          return <Notifications />;
        case "settings":
          return <Settings />;
        default:
          return <SystemAdminDashboard />;
      }
    } else if (user.role === "COLLEGE_ADMIN") {
      // For COLLEGE_ADMIN, use the modern CollegeAdminLayout with sections
      switch (currentSection) {
        case "analytics":
          // return <CollegeAnalytics />;
          return <AccountantAnalytics />;
        case "dashboard":
          return <CollegeAdminDashboard />;
        case "students":
          return <Students />;
        case "groups":
          return <CollegeGroups />;
        case "users":
          return <Users />;
        case "payments":
          return <CollegePayments />;
        case "reports":
          return <CollegeReports />;
        case "notifications":
          return <CollegeNotifications />;
        case "settings":
          return <CollegeSettings />;
        default:
          return <CollegeAdminDashboard />;
      }
    } else if (user.role === "STAFF") {
      // For STAFF, use the modern StaffLayout with sections
      switch (currentSection) {
        case "analytics":
          return <StaffAnalytics />;
        case "groups":
          return <CollegeGroups />;
        case "dashboard":
          return <StaffDashboard />;
        case "students":
          return <Students />;
        case "reports":
          return <StaffReports />;
        case "settings":
          return <StaffSettings />;
        default:
          return <StaffDashboard />;
      }
    } else if (user.role === "ACCOUNTANT") {
      // For ACCOUNTANT, use the modern AccountantAdminLayout with sections
      switch (currentSection) {
        case "analytics":
          return <AccountantAnalytics />;
        case "groups":
          return <CollegeGroups />;  
        case "dashboard":
          return <AccountantDashboard />;
        case "students":
          return <Students />;
        case "payments":
          return <AccountantPayments />;
        case "reports":
          return <AccountantReports />;
        case "notifications":
          return <AccountantNotifications />;
        case "settings":
          return <AccountantSettings />;
        default:
          return <AccountantDashboard />;
      }
    } else {
      // For other roles, show their respective dashboards
      return getLegacyDashboardContent();
    }
  };

  // Modern System Admin Dashboard Content
  const SystemAdminDashboard = () => (
    <div className="flex flex-col gap-4 md:gap-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-xl font-bold">Welcome back, {user.firstName} 👋</h1>
        <p className="text-sm">Here's your system overview for today</p>
      </div>
      <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Colleges"
          value={collegePagination.total.toString()}
          icon={Building}
        />
        <StatsCard
          title="Total Students"
          value={studentCount?.toLocaleString() || "0"}
          icon={UsersIcon}
        />
        <StatsCard
          title="Total Revenue"
          value={`₹${totalRevenue?.toLocaleString() || "0"}`}
          icon={IndianRupee}
        />
        <StatsCard
          title="Transactions"
          value={totalPayments?.toLocaleString() || "0"}
          icon={TrendingUp}
        />
      </div>

      {/* Users Section */}
      <div>
        <h3 className="text-md font-medium text-gray-800 mb-2">Users</h3>
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Users"
            value={userStats.total.toString()}
            icon={UsersIcon}
          />
          <StatsCard
            title="Active Users"
            value={userStats.active.toString()}
            icon={UsersIcon}
          />
          <StatsCard
            title="System Admins"
            value={(userStats.byRole.SYSTEM_ADMIN || 0).toString()}
            icon={Shield}
          />
          <StatsCard
            title="College Admins"
            value={(userStats.byRole.COLLEGE_ADMIN || 0).toString()}
            icon={Building}
          />
        </div>
      </div>
    </div>
  );

  // Modern College Admin Dashboard Content
  const CollegeAdminDashboard = () => (
    <div className="flex flex-col gap-4 md:gap-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-xl font-bold">Welcome back, {user.firstName} 👋</h1>
        <p className="text-sm">Here's your college overview for today</p>
      </div>
      {/* Input-style container with overlapping label */}
      <div className="relative mt-2">
        <div
          className="w-full rounded-xl border border-gray-300 bg-card p-6"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, transparent, transparent 2px, #f3f4f6 2px, #f3f4f6 4px)",
          }}
        >
          <div className="flex flex-col gap-3 pt-4">
            {/* Stats Cards */}
            <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
              <StatsCard
                title="Active Revenue"
                value={formatCurrency(collegeDashboardMetrics?.totalFees || 0)}
                icon={IndianRupee}
              />
              <StatsCard
                title="Active Students"
                value={
                  collegeDashboardMetrics?.activeStudents?.toLocaleString() ||
                  "0"
                }
                icon={UsersIcon}
              />
              <StatsCard
                title="Active Staff"
                value={userStats.active.toLocaleString() || "0"}
                icon={UsersIcon}
              />
              <StatsCard
                title="Active Groups"
                value={
                  collegeDashboardMetrics?.activePrograms?.toLocaleString() ||
                  "0"
                }
                icon={BookOpen}
              />
            </div>
            <div className="flex flex-col gap-y-2">
              <h3 className="text-md font-medium text-gray-800">Revenue</h3>
              <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
                <StatsCard
                  title="Total Paid"
                  value={formatCurrency(
                    collegeDashboardMetrics?.totalPaid || 0
                  )}
                  icon={IndianRupee}
                />
                <StatsCard
                  title="Pending amount"
                  value={formatCurrency(
                    collegeDashboardMetrics?.pendingAmount || 0
                  )}
                  icon={Hourglass}
                />
                <StatsCard
                  title="Today's Collection"
                  value={formatCurrency(
                    collegeDashboardMetrics?.collectionsToday || 0
                  )}
                  icon={Calendar}
                />
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <h3 className="text-md font-medium text-gray-800">Staff</h3>
              <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
                <StatsCard
                  title="Faculty Members"
                  value={(userStats.byRole.FACULTY || 0).toString()}
                  icon={UsersIcon}
                />
                <StatsCard
                  title="Account Managers"
                  value={(userStats.byRole.ACCOUNTANT || 0).toString()}
                  icon={Shield}
                />
                <StatsCard
                  title="Enrollment Staff"
                  value={(userStats.byRole.STAFF || 0).toString()}
                  icon={UsersIcon}
                />
                <StatsCard
                  title="College Admins"
                  value={(userStats.byRole.COLLEGE_ADMIN || 0).toString()}
                  icon={Shield}
                />
              </div>
            </div>
          </div>
        </div>
        <label className="absolute -top-[0.75rem] text-white bg-black left-3 px-2 py-1 text-sm font-medium rounded-sm">
          Current Academic Year Overview
        </label>
      </div>

      <div className="flex flex-col gap-3">
        <h1 className="text-md font-medium">Institution Legacy Overview</h1>
        {/* Stats Cards */}
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Students"
            value={
              collegeDashboardMetrics?.totalStudents?.toLocaleString() || "0"
            }
            icon={UsersIcon}
          />
          <StatsCard
            title="Total Revenue"
            value={formatCurrency(collegeDashboardMetrics?.totalRevenue || 0)}
            icon={IndianRupee}
          />
          <StatsCard
            title="Total Staff"
            value={userStats.total.toLocaleString() || "0"}
            icon={UsersIcon}
          />
          <StatsCard
            title="Total Groups"
            value={
              collegeDashboardMetrics?.totalPrograms?.toLocaleString() || "0"
            }
            icon={BookOpen}
          />
        </div>
      </div>
      
      {/* Payment Records Section */}
      <div className="mt-6">
        <PaymentRecordsTable />
      </div>
    </div>
  );

  const AccountantDashboard = () => (
    <div className="flex flex-col gap-4 md:gap-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-xl font-bold">Welcome back, {user.firstName} 👋</h1>
        <p className="text-sm">Here's your college overview for today</p>
      </div>
      <PaymentRecordsTable />
    </div>
  );

  // Coming Soon Component
  const ComingSoon = ({ title }: { title: string }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-12 rounded-xl shadow-lg text-center"
    >
      <div className="text-6xl mb-4">🚧</div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">{title}</h2>
      <p className="text-gray-600 mb-6">This feature is coming soon!</p>
      <p className="text-sm text-gray-500">
        We're working hard to bring you this functionality.
      </p>
    </motion.div>
  );

  // Legacy dashboard content for other roles
  const getLegacyDashboardContent = () => {
    switch (user.role) {
      case "COLLEGE_ADMIN":
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <header className="bg-white border-b border-slate-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    Welcome, {user.firstName} {user.lastName}
                  </h1>
                  <p className="text-gray-600">Role: {user.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </header>

            <main className="p-6">
              <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    Dashboard
                  </h2>
                  <p className="text-gray-600">
                    Overview of your college administration activities
                  </p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  <DashboardCard
                    title="Students"
                    value={studentCount?.toLocaleString() || "0"}
                    description="College students"
                    color="bg-green-500"
                    icon="👨‍🎓"
                  />
                  <DashboardCard
                    title="Payments"
                    value={totalPayments?.toLocaleString() || "0"}
                    description="Total transactions"
                    color="bg-blue-500"
                    icon="💰"
                  />
                  <DashboardCard
                    title="Revenue"
                    value={`₹${totalRevenue?.toLocaleString() || "0"}`}
                    description="Total collected"
                    color="bg-purple-500"
                    icon="📊"
                  />
                </motion.div>
              </div>
            </main>
          </div>
        );
      case "STUDENT":
        const studentPayments = payments.filter(
          (p) => p.student.rollNumber === user.id
        );
        const totalPaid = studentPayments.reduce(
          (sum, payment) => sum + Number(payment.amountPaid),
          0
        );

        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <header className="bg-white border-b border-slate-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    Welcome, {user.firstName} {user.lastName}
                  </h1>
                  <p className="text-gray-600">Role: {user.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </header>

            <main className="p-6">
              <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    Dashboard
                  </h2>
                  <p className="text-gray-600">
                    Overview of your student activities
                  </p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Fee Status
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Paid:</span>
                        <span className="font-semibold text-green-600">
                          ₹{totalPaid.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Transactions:</span>
                        <span className="font-semibold">
                          {studentPayments.length}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Payment:</span>
                        <span className="font-semibold">
                          {studentPayments.length > 0
                            ? new Date(
                                studentPayments[0].paymentDate
                              ).toLocaleDateString()
                            : "No payments"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Profile Info
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Name:</span>
                        <span className="font-semibold">
                          {user.firstName} {user.lastName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-semibold">{user.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">College:</span>
                        <span className="font-semibold">
                          {user.college?.name || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </main>
          </div>
        );
      default:
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <header className="bg-white border-b border-slate-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    Welcome, {user.firstName} {user.lastName}
                  </h1>
                  <p className="text-gray-600">Role: {user.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </header>

            <main className="p-6">
              <div className="max-w-7xl mx-auto">
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">
                    Dashboard
                  </h2>
                  <p className="text-gray-600">Overview of your activities</p>
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white p-6 rounded-xl shadow-lg"
                >
                  <h3 className="text-lg font-semibold text-gray-800">
                    Welcome!
                  </h3>
                  <p className="text-gray-600">
                    Dashboard content for {user.role} is being prepared.
                  </p>
                </motion.div>
              </div>
            </main>
          </div>
        );
    }
  };

  // For SYSTEM_ADMIN, use AdminLayout; for COLLEGE_ADMIN, use CollegeAdminLayout; for others, use legacy layout
  if (user.role === "SYSTEM_ADMIN") {
    return (
      <AdminLayout
        currentSection={currentSection}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
      >
        {renderContent()}
      </AdminLayout>
    );
  }

  if (user.role === "COLLEGE_ADMIN") {
    return (
      <CollegeAdminLayout
        currentSection={currentSection}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
      >
        {renderContent()}
      </CollegeAdminLayout>
    );
  }

  if (user.role === "STAFF") {
    return (
      <StaffLayout
        currentSection={currentSection}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
      >
        {renderContent()}
      </StaffLayout>
    );
  }

  if (user.role === "ACCOUNTANT") {
    return (
      <AccountantAdminLayout
        currentSection={currentSection}
        onSectionChange={handleSectionChange}
        onLogout={handleLogout}
        user={user}
      >
        {renderContent()}
      </AccountantAdminLayout>
    );
  }

  // For other roles, render legacy dashboard
  return renderContent();
};

// Stats Card Component
export interface StatsCardProps {
  title: string;
  value: string;
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
  >;
}

export const StatsCard = ({ title, value, icon }: StatsCardProps) => {
  const StatsCardIcon = icon;
  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          {value}
        </CardTitle>
        <CardAction>
          <StatsCardIcon className="w-4 h-4" />
        </CardAction>
      </CardHeader>
    </Card>
  );
};

// Quick Action Card Component
interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
  >;
  onClick: () => void;
}

const QuickActionCard = ({
  title,
  description,
  icon,
  onClick,
}: QuickActionCardProps) => {
  const QuickActionIcon = icon;
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      className="text-left p-6 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition-all"
    >
      <QuickActionIcon className="h-4 w-4 text-muted-foreground mb-4" />
      <h4 className="font-semibold text-gray-800 text-lg">{title}</h4>
      <p className="text-xs text-slate-500">{description}</p>
    </motion.button>
  );
};

// Legacy Dashboard Card Component for other roles
interface DashboardCardProps {
  title: string;
  value: string;
  description: string;
  color: string;
  icon: string;
}

const DashboardCard = ({
  title,
  value,
  description,
  color,
  icon,
}: DashboardCardProps) => {
  return (
    <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div
          className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center text-white text-xl`}
        >
          {icon}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-1">{value}</h3>
      <p className="text-lg font-semibold text-gray-700 mb-1">{title}</p>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
};

export default Dashboard;
